import { useMemo } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import moment from "moment";
import { twMerge } from "tailwind-merge";

import { PrimaryButton } from "@components/v2/buttons";
import {
  ActiveTab,
  PrettyActiveTab,
  useClearBudgetMixUrlState,
  useMMMActiveTab,
} from "@features/mmm/hooks/mmm-url-state";
import { useAdoptedBudgetMix } from "@features/mmm/hooks/use-adopted-budget-mix";
import { formatDate } from "@features/mmm/utils/formatters";
import { useFeatureFlags } from "@hooks/http";
import { MMMClientState, type MMMClientCycleBase } from "@north-beam/nb-common";
import { CommonTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useClientCycles } from "@pages/mmm/hooks/use-client-cycles";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";
import { ListBox } from "@shared/list-box";

import { StepperStatePopover } from "./stepper-state-popover";

function createSimulatedClientState(
  currentState: MMMClientState,
  hasCreatedSettings: boolean,
  hasAdoptedMix: boolean,
) {
  if (currentState !== MMMClientState.TRACKING_MIX) {
    return currentState;
  }

  if (hasAdoptedMix) {
    // Note: Do this first in case a custom mix has been adopted and
    // no settings were needed
    return MMMClientState.TRACKING_MIX;
  } else {
    if (!hasCreatedSettings) {
      return MMMClientState.CREATING_SETTINGS;
    }

    return MMMClientState.ADOPTING_MIX;
  }
}

export const TopBar = () => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.ALL_TABS);
  const { selectedCycle, setSelectedCycle } = useSelectedCycle();
  const [clientCycles] = useClientCycles();

  const clearMMMUrlState = useClearBudgetMixUrlState();

  const handleSelectCycle = (cycle: MMMClientCycleBase) => {
    trackMMMEvent({
      component: CommonTrackedComponent.CYCLE_SELECTOR,
      action: "onChange",
    });

    clearMMMUrlState();
    setSelectedCycle(cycle);
  };

  const { setActiveTab } = useMMMActiveTab();
  const { hasFeatureFlag } = useFeatureFlags();
  const enableMmmMediaPlanTab = hasFeatureFlag("enableMmmMediaPlanTab");

  return (
    <div className="px-6 py-4 flex justify-between space-x-4 bg-v2-primary border-b border-v2-secondary text-v2-primary">
      <div className="flex items-end space-x-3 grow justify-between">
        <div>
          <div className="text-sm flex items-center gap-2">
            Forecast Period <CyclePill cycle={selectedCycle} />
          </div>
          <div className="flex items-center justify-start gap-3">
            <div className="text-2xl font-bold min-w-[150px] truncate">
              {formatDate(selectedCycle.date_range.start_date)} -{" "}
              {formatDate(selectedCycle.date_range.end_date)}{" "}
              {selectedCycle.name ? `(${selectedCycle.name})` : ""}
            </div>
            <MMMCycleDropdown
              selectedCycle={selectedCycle}
              onSelectCycle={handleSelectCycle}
              cycles={clientCycles}
              zClassName="z-[29]"
            />
            {selectedCycle.hidden && (
              <Pill
                text="Hidden from Customer"
                type={PillType.WARNING}
                className="text-md"
              />
            )}
          </div>
        </div>
        {enableMmmMediaPlanTab && (
          <PrimaryButton
            className="h-8"
            onClick={() => setActiveTab(ActiveTab.MEDIA_PLAN)}
          >
            {PrettyActiveTab[ActiveTab.MEDIA_PLAN]}
          </PrimaryButton>
        )}
      </div>
      <div className="justify-self-end self-end">
        <MMMCycleStage cycle={selectedCycle} />
      </div>
    </div>
  );
};

export const StatesToActionMapping: Record<MMMClientState, string | null> = {
  [MMMClientState.POPULATING_DATA]: "Upload data",
  [MMMClientState.REVIEWING_MODEL]: null,
  [MMMClientState.CREATING_SETTINGS]: "Create settings",
  [MMMClientState.PREPARING_MIX]: null,
  [MMMClientState.ADOPTING_MIX]: "Adopt budget",
  [MMMClientState.TRACKING_MIX]: null,
  [MMMClientState.REVIEWING_PERIOD]: null,
  [MMMClientState.NOT_SET]: null,
};

export const MMM_PRETTY_STATES: Record<MMMClientState, string | null> = {
  [MMMClientState.POPULATING_DATA]: "Upload data",
  [MMMClientState.REVIEWING_MODEL]: "Upload data",
  [MMMClientState.CREATING_SETTINGS]: "Create settings",
  [MMMClientState.PREPARING_MIX]: "Preparing Budget",
  [MMMClientState.ADOPTING_MIX]: "Adopt Budget",
  [MMMClientState.TRACKING_MIX]: "Tracking Budget",
  [MMMClientState.REVIEWING_PERIOD]: "Reviewing",
  [MMMClientState.NOT_SET]: null,
};

export const CLIENT_STATE_MAPPING_TO_INDEX = {
  [MMMClientState.NOT_SET]: 0,
  [MMMClientState.POPULATING_DATA]: 0,
  [MMMClientState.REVIEWING_MODEL]: 0,
  [MMMClientState.CREATING_SETTINGS]: 1,
  [MMMClientState.PREPARING_MIX]: 2,
  [MMMClientState.ADOPTING_MIX]: 3,
  [MMMClientState.TRACKING_MIX]: 4,
  [MMMClientState.REVIEWING_PERIOD]: 4,
};

const LAST_STEP_INDEX = 4;

export function MMMCycleStage({
  cycle: actualCycle,
}: {
  cycle: MMMClientCycleBase;
}) {
  const { adoptedBudgetMix } = useAdoptedBudgetMix();
  const { clientSettings } = useClientSettings();

  const cycle = {
    ...actualCycle,
    state: createSimulatedClientState(
      actualCycle.state,
      clientSettings.length > 0,
      !!adoptedBudgetMix,
    ),
  };
  const clientMappingIndex = CLIENT_STATE_MAPPING_TO_INDEX[cycle.state];

  if (clientMappingIndex === LAST_STEP_INDEX) {
    return (
      <StepperStatePopover cycleState={cycle.state}>
        <div className="text-v2-action flex items-center space-x-2 text-xs font-semibold rounded-2xl bg-v2-primary border border-v2-light h-8 px-4">
          <i className="fa-regular fa-check" />
          <div>Setup complete</div>
        </div>
      </StepperStatePopover>
    );
  }

  return (
    <StepperStatePopover cycleState={cycle.state}>
      <div className="h-[2rem] rounded-2xl bg-v2-secondary px-4 py-1 flex items-center">
        <div className="flex items-center space-x-1 relative">
          {Array.from({ length: 5 }).map((_, index, arr) => {
            const isLastStep = clientMappingIndex === arr.length - 1;
            if (index < clientMappingIndex || isLastStep) {
              return (
                <div
                  className="z-2 rounded-full bg-alien+0 w-3 h-3 relative"
                  key={index}
                >
                  <div
                    className="rounded-full bg-v2-primary w-1 h-1 absolute translate-y-1 translate-x-1 "
                    key={index}
                  />
                </div>
              );
            }

            if (clientMappingIndex === index) {
              return (
                <div
                  className={clsx(
                    "z-2 rounded-full bg-primary w-5 h-5 font-semibold text-xs text-white",
                    "flex items-center justify-center",
                  )}
                  key={index}
                >
                  {index + 1}
                </div>
              );
            }

            return (
              <div
                className="z-2 rounded-full bg-gray w-3 h-3 relative"
                key={index}
              ></div>
            );
          })}
          <div className="z-0 absolute left-0 right-0 h-1 bg-gray-1" />
        </div>
        <div className="ml-2 text-v2-primary text-center text-sm">
          {MMM_PRETTY_STATES[cycle.state]}
        </div>
      </div>
    </StepperStatePopover>
  );
}

export const MMMCycleDropdown = ({
  selectedCycle,
  onSelectCycle,
  cycles,
  buttonSize = "base",
  zClassName = "z-[40]",
}: {
  selectedCycle: MMMClientCycleBase;
  onSelectCycle: (cycle: MMMClientCycleBase) => void;
  cycles: MMMClientCycleBase[];
  buttonSize?: "base" | "sm";
  zClassName?: string;
}) => {
  const sortedCycles = useMemo(() => {
    const sorted = [...cycles];
    sorted.sort((c1, c2) => {
      const startDate1 = moment(c1.date_range.start_date, "YYYY-MM-DD");
      const startDate2 = moment(c2.date_range.start_date, "YYYY-MM-DD");
      if (startDate1.isBefore(startDate2)) {
        return 1;
      } else if (startDate1.isAfter(startDate2)) {
        return -1;
      }

      return 0;
    });

    return sorted;
  }, [cycles]);

  const upcomingAndCurrentCycles = useMemo(() => {
    return sortedCycles.filter((cycle) => {
      const now = moment();
      const cycleEndDate = moment(cycle.date_range.end_date, "YYYY-MM-DD");
      return now.isSameOrBefore(cycleEndDate);
    });
  }, [sortedCycles]);

  const previousCycles = useMemo(() => {
    return sortedCycles.filter((cycle) => {
      const now = moment();
      const cycleEndDate = moment(cycle.date_range.end_date, "YYYY-MM-DD");
      return now.isAfter(cycleEndDate);
    });
  }, [sortedCycles]);

  return (
    <ListBox
      menuClassName={clsx(
        "w-[22rem] max-h-[25rem] overflow-y-auto",
        zClassName,
      )}
      menuPosition="bottom-right"
      value={selectedCycle}
      setValue={onSelectCycle}
      menuButton={
        <ListBox.Button as="div">
          {({ open }) => (
            <button
              className={clsx(
                "rounded-full border border-v2-secondary text-sm font-bold text-primary flex-center",
                {
                  "bg-cosmic border-cosmic": open,
                  "hover:bg-cosmic-3": !open,
                  "h-8 w-8": buttonSize === "base",
                  "h-6 w-6": buttonSize === "sm",
                },
              )}
            >
              <motion.i
                initial={false}
                animate={{ rotate: open ? 0 : 180 }}
                transition={{ duration: 0.2 }}
                className={clsx("fa-sharp fa-solid fa-chevron-up", {
                  "text-v2-action": !open,
                  "text-white": open,
                })}
              />
            </button>
          )}
        </ListBox.Button>
      }
    >
      <div className="flex flex-col gap-2 justify-start items-start py-2 px-4 mt-2">
        <span className="text-xxs text-gray">Current and upcoming</span>
      </div>
      {upcomingAndCurrentCycles.length ? (
        upcomingAndCurrentCycles.map((cycle) => (
          <ClientCycle key={cycle.id} cycle={cycle} />
        ))
      ) : (
        <div className="px-4 pt-1 pb-2 text-sm italic ">
          No current or upcoming cycles
        </div>
      )}
      {previousCycles.length > 0 && (
        <div className="pt-5 pb-1 px-4">
          <span className="text-gray text-xxs flex items-center gap-2">
            <i className="fa-regular fa-check"></i> Historicals
          </span>
        </div>
      )}
      {previousCycles.map((cycle) => (
        <ClientCycle key={cycle.id} cycle={cycle} />
      ))}
    </ListBox>
  );
};

function ClientCycle({ cycle }: { cycle: MMMClientCycleBase }) {
  const { adoptedBudgetMix } = useAdoptedBudgetMix();
  const formatDates = (start_date: string, end_date: string) =>
    `${formatDate(start_date)} - ${formatDate(end_date)}`;

  const actionText = useMemo(() => {
    if (cycle.is_errored) {
      return null;
    }
    const cycleState = cycle.state;
    const action = StatesToActionMapping[cycle.state];
    if (
      cycleState === MMMClientState.CREATING_SETTINGS &&
      !cycle?.clientSettingsCount
    ) {
      return action;
    }
    if (cycleState === MMMClientState.ADOPTING_MIX && !adoptedBudgetMix) {
      return action;
    }
    return null;
  }, [
    adoptedBudgetMix,
    cycle?.clientSettingsCount,
    cycle.is_errored,
    cycle.state,
  ]);

  return (
    <ListBox.Option
      className={clsx(
        "hover:bg-cosmic-4 py-2 px-4 flex justify-between items-center gap-6 cursor-pointer",
        "group/row",
      )}
      as="div"
      value={cycle}
    >
      <div className="flex flex-col justify-start items-start ">
        <span className="text-base group-hover/row:font-bold ">
          {formatDates(cycle.date_range.start_date, cycle.date_range.end_date)}{" "}
        </span>
        {cycle.name && (
          <span className="text-xxs text-subtitle truncate">{cycle.name}</span>
        )}
        {actionText && (
          <span className="text-xxs text-mars">
            Action required • {actionText}
          </span>
        )}
      </div>
      <div className="flex space-x-1">
        {cycle.hidden && <Pill text="Hidden" type={PillType.WARNING} />}
        <CyclePill cycle={cycle} />
      </div>
    </ListBox.Option>
  );
}

export function CyclePill({ cycle }: { cycle: MMMClientCycleBase }) {
  const today = moment();
  const startDate = moment(cycle.date_range.start_date, "YYYY-MM-DD");
  const endDate = moment(cycle.date_range.end_date, "YYYY-MM-DD");
  const isCurrent =
    today.isSameOrAfter(startDate) && today.isSameOrBefore(endDate);

  const isUpcoming = startDate.isAfter(today);
  const hasPill = isUpcoming || isCurrent || cycle.is_errored;

  const getPillType = () => {
    if (isCurrent) {
      return PillType.INFO;
    }
    if (isUpcoming) {
      return PillType.WARNING;
    }

    return PillType.DANGER;
  };

  const getPillText = () => {
    if (isCurrent) {
      return "Current";
    }
    if (isUpcoming) {
      return "Upcoming";
    }

    return "Failed";
  };

  if (!hasPill) {
    return null;
  }

  return <Pill className="h-fit" text={getPillText()} type={getPillType()} />;
}

export enum PillType {
  INFO,
  WARNING,
  DANGER,
}

export function Pill({
  text,
  type,
  className,
}: {
  text: string;
  type: PillType;
  className?: string;
}) {
  return (
    <span
      className={twMerge(
        "text-xs py-1 px-2 rounded-md",
        clsx({
          "bg-v2-highlight-notice text-v2-action-positive":
            type === PillType.INFO,
          "bg-v2-highlight-pending text-[#996800]": type === PillType.WARNING,
          "bg-v2-hightlight-alert text-v2-action-negative":
            type === PillType.DANGER,
        }),
        className,
      )}
    >
      {text}
    </span>
  );
}
